import type {
  IChatHistoryPage,
  IChatHistoryPagesContainer,
  IFindMessageLocationInPagesProps,
  IHasStatus,
  IMapMessagesInChatHistoryProps,
  IMessage,
  IMessageLocationInPages,
  IMessageToolCall,
  IResolveMessageToolCallProps,
  IUpdateFirstPageInChatHistoryProps,
  IUpdateMessageContentInPagesProps,
  IUpdateMessageInChatHistoryProps,
} from "../types/chat";

// NOTE: NSAI-5908 — copy-on-write updates for the chat-history query cache.
//
// Every streamed socket frame used to be applied as `structuredClone(oldData)`
// followed by an in-place mutation: one deep copy of the WHOLE transcript
// (every page, every message, base64 attachments included) per model token,
// then a second full pass by React Query's structural sharing. The helpers
// here rebuild only the path root → pages → touched page → data → touched
// message and leave every other page and message by reference, so the cost of
// a frame is proportional to the message it touches, not to the transcript —
// and unchanged messages keep their identity, which is what lets the
// transcript rows memoize.
//
// Shared by web and mobile: both hold the same InfiniteData-shaped cache and
// both used to clone it wholesale (see the mobile hook), so a second copy of
// this logic is exactly the drift the parity rule exists to prevent.

export const findMessageLocationInPages = ({
  pages,
  uuid,
}: IFindMessageLocationInPagesProps): IMessageLocationInPages | null => {
  for (let pageIndex = 0; pageIndex < pages.length; pageIndex += 1) {
    const messageIndex = pages[pageIndex].data.findIndex(
      (message) => message.uuid === uuid,
    );
    if (messageIndex !== -1) {
      return { pageIndex, messageIndex };
    }
  }
  return null;
};

const replacePage = <TData extends IChatHistoryPagesContainer>(params: {
  oldData: TData;
  pageIndex: number;
  page: IChatHistoryPage;
}): TData => {
  const { oldData, pageIndex, page } = params;
  const pages = [...oldData.pages];
  pages[pageIndex] = page;
  return { ...oldData, pages };
};

// NOTE: Rebuilds only the path to ONE message. The mutator receives the clone
// AND the rebuilt pages array (the clone already sits in it), so the existing
// `pages`-based helpers keep working unchanged. Returns `oldData` itself when
// the message is missing or the mutator reports no change, so React Query's
// observers are not notified for a no-op.
export const updateMessageInChatHistory = <
  TData extends IChatHistoryPagesContainer,
>({
  oldData,
  uuid,
  cloneMessage,
  mutate,
}: IUpdateMessageInChatHistoryProps<TData>): TData => {
  const location = findMessageLocationInPages({ pages: oldData.pages, uuid });
  if (!location) {
    return oldData;
  }

  const { pageIndex, messageIndex } = location;
  const page = oldData.pages[pageIndex];
  const clonedMessage = cloneMessage(page.data[messageIndex]);

  const data = [...page.data];
  data[messageIndex] = clonedMessage;
  const pages = [...oldData.pages];
  pages[pageIndex] = { ...page, data };

  const hasChanged = mutate({ message: clonedMessage, pages });
  if (!hasChanged) {
    return oldData;
  }

  return { ...oldData, pages };
};

// NOTE: Optimistic temps, `newMessages` and temp removal only ever touch the
// first (most recent) page, so only that page's `data` array is rebuilt.
export const updateFirstPageInChatHistory = <
  TData extends IChatHistoryPagesContainer,
>({
  oldData,
  update,
}: IUpdateFirstPageInChatHistoryProps<TData>): TData => {
  const firstPage = oldData.pages[0];
  if (!firstPage) {
    return oldData;
  }

  const nextData = update(firstPage.data);
  if (nextData === null || nextData === firstPage.data) {
    return oldData;
  }

  return replacePage({
    oldData,
    pageIndex: 0,
    page: { ...firstPage, data: nextData },
  });
};

// NOTE: A chat-wide pass (e.g. cancelling every search-progress indicator on
// stop) that still keeps the reference of every message the mapper leaves
// alone, and of every page holding only such messages.
export const mapMessagesInChatHistory = <
  TData extends IChatHistoryPagesContainer,
>({
  oldData,
  map,
}: IMapMessagesInChatHistoryProps<TData>): TData => {
  let hasAnyUpdates = false;
  const pages: IChatHistoryPage[] = [];

  for (const page of oldData.pages) {
    let hasPageUpdates = false;
    const data: IMessage[] = [];

    for (const message of page.data) {
      const replacement = map(message);
      if (replacement === null) {
        data.push(message);
        continue;
      }
      data.push(replacement);
      hasPageUpdates = true;
      hasAnyUpdates = true;
    }

    pages.push(hasPageUpdates ? { ...page, data } : page);
  }

  if (!hasAnyUpdates) {
    return oldData;
  }

  return { ...oldData, pages };
};

// NOTE: NSAI-5909 — what a `toolCall` frame leaves on the message. Arguments
// arrive as partial JSON spread over several frames, so a frame for a call
// already in flight carries only the keys parsed since the last one; replacing
// would leave the UI holding the final fragment rather than the whole call. A
// frame for a different tool starts fresh, and `_completed` clears the call.
// Lives here because web and mobile both apply it, and two copies of the rule
// is the drift this module exists to prevent.
export const resolveMessageToolCall = ({
  toolName,
  args,
  priorTool,
}: IResolveMessageToolCallProps): IMessageToolCall | undefined =>
  args._completed
    ? undefined
    : {
        name: toolName,
        args:
          priorTool?.name === toolName ? { ...priorTool.args, ...args } : args,
      };

// NOTE: NSAI-5909 — a full-content replacement frame (PII deanonymization, and
// the reconnect re-sync). Two guards decide whether the write lands at all, and
// both matter: a message the server already called done is never reopened by a
// replayed frame, and an empty incoming body never erases text already on
// screen. Lives here because web and mobile applied the identical rule from two
// copies, which is the drift this module exists to prevent.
export const updateMessageContentInPages = ({
  pages,
  uuid,
  content,
}: IUpdateMessageContentInPagesProps): boolean => {
  for (const page of pages) {
    const message = page.data.find((msg: IMessage) => msg.uuid === uuid);
    if (message) {
      // NOTE: On refresh/reconnect the server may replay buffered content for
      // in-flight streams. Never let that overwrite a completed message — the
      // DB is the source of truth once `done` is true.
      if (message.done) {
        return false;
      }

      // NOTE: Some providers can transiently emit an empty full-content update.
      // Never allow an empty replace to erase already-streamed content.
      const isEmptyIncoming = content.trim().length === 0;
      const hasExistingContent = message.content.trim().length > 0;
      if (isEmptyIncoming && hasExistingContent) {
        return false;
      }

      // NOTE: REPLACE, not append.
      message.content = content;
      message.done = false;
      // NOTE: Incoming stream content means generation is active again.
      delete message.streamError;
      message.streamFinished = false;
      return true;
    }
  }
  return false;
};

const cancelProgressStatus = <T extends IHasStatus>(progress: T): T => ({
  ...progress,
  status: "cancelled",
});

const cancelQueriesStatus = <T extends IHasStatus>(queries: T[]): T[] =>
  queries.map((query) => ({
    ...query,
    status: query.status === "completed" ? query.status : "cancelled",
  }));

// NOTE: NSAI-5909 — the one place that knows what "cancelled" means for search
// progress: a replacement message when it carried any, `null` when it did not,
// so the callers keep its reference. Shared for the same reason as the rule
// above — web and mobile each held a byte-identical copy.
export const cancelSearchProgressOnMessage = (
  message: IMessage,
): IMessage | null => {
  const hasSearchProgress =
    !!message.deepSearchProgress ||
    !!message.deepSearchQueries?.length ||
    !!message.webSearchProgress ||
    !!message.webSearchQueries?.length;
  if (!hasSearchProgress) {
    return null;
  }

  return {
    ...message,
    ...(message.deepSearchProgress && {
      deepSearchProgress: cancelProgressStatus(message.deepSearchProgress),
    }),
    ...(message.deepSearchQueries?.length && {
      deepSearchQueries: cancelQueriesStatus(message.deepSearchQueries),
    }),
    ...(message.webSearchProgress && {
      webSearchProgress: cancelProgressStatus(message.webSearchProgress),
    }),
    ...(message.webSearchQueries?.length && {
      webSearchQueries: cancelQueriesStatus(message.webSearchQueries),
    }),
  };
};
